/**
 * Guarantees of a contract or a vehicle (« Garanties » tab — fiches 22717 /
 * 22768): a `DataList` with the stable columns `libelle` (LIB_GAR), `plafond`
 * (CAP_GARP) and `franchise` (FRANCHISE / FRA_GARP) — amounts in €, 2
 * decimals, `null` ⇒ empty cell — rows in API order.
 *
 * Rows are mapped by the domain service (view model `GuaranteeTableRow`); the
 * columns live in `GUARANTEES_TABLE_COLUMNS` (`./guaranteesTableConfig.ts`).
 * Overrides / publish / states are handled by `DataTableCard`.
 *
 * NOT registered by itself — registered per usage
 * (`extranet.contractGuaranteesCard`, `extranet.vehicleGuaranteesCard`).
 */

import { useMemo } from 'react';
import { ShieldCheck, type LucideIcon } from 'lucide-react';

import type { LayoutOverrides } from '@components/shared/configurable';

import { DataTableCard } from './DataTableCard';
import { buildGuaranteesTableConfig, type GuaranteeTableRow } from './guaranteesTableConfig';

export type { GuaranteeTableRow } from './guaranteesTableConfig';

export interface GuaranteesTableCardProps {
  /** Registered id of this usage (bound by `bindCardProps`). */
  componentId: string;
  /** Base title (overridable); omitted ⇒ no title row. */
  title?: string;
  icon?: LucideIcon;
  rows: readonly GuaranteeTableRow[];
  loading?: boolean;
  error?: string | null;
  /** Injected by `withComponentRules` — never passed by hand. */
  _layoutOverrides?: LayoutOverrides;
}

export function GuaranteesTableCard({
  componentId,
  title,
  icon = ShieldCheck,
  rows,
  loading = false,
  error = null,
  _layoutOverrides,
}: Readonly<GuaranteesTableCardProps>) {
  const config = useMemo(
    () => buildGuaranteesTableConfig(componentId, rows),
    [componentId, rows],
  );

  return (
    <DataTableCard
      componentId={componentId}
      title={title}
      icon={icon}
      config={config}
      rowCount={rows.length}
      loading={loading}
      error={error}
      _layoutOverrides={_layoutOverrides}
    />
  );
}
